"use client";

import { useEffect, useRef } from "react";

import { trackEvent } from "@/utils/analytics";

interface ImpressionEvent {
  eventName: string;
  params: Record<string, string | number | boolean | undefined>;
}

/**
 * 단일 요소의 뷰포트 노출 추적
 * threshold(기본 50%) 이상 노출되면 이벤트 1회 발송
 */
export function useImpressionTracking<T extends HTMLElement>(config: ImpressionEvent, threshold = 0.5) {
  const ref = useRef<T>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || firedRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          trackEvent(config.eventName, { ...config.params, viewport_percent: 50 });
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [config.eventName, config.params, threshold]);

  return ref;
}

/**
 * 단일 요소 노출 시 여러 이벤트를 동시에 발송
 * 예: GNB 컨테이너가 보이면 모든 메뉴 아이템의 impression 이벤트를 한번에 발송
 */
export function useBatchImpressionTracking<T extends HTMLElement>(events: ImpressionEvent[], threshold = 0.5) {
  const ref = useRef<T>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || firedRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          for (const event of events) {
            trackEvent(event.eventName, { ...event.params, viewport_percent: 50 });
          }
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [events, threshold]);

  return ref;
}
